import {
  GiftDeliveryDatePolicy,
  GiftItemBrand,
} from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { kstDateOf, kstShiftDay, kstTimeHHmm } from '@shared/utils/kstTime.util';

// ─────────────────────────────────────────────
// 배송 희망일 지정 (떡보의하루 등) — 브랜드 deliveryDetail.datePolicy 기준.
// 마감시각 전 결제는 오늘+leadDaysBeforeCutoff일부터, 이후는 오늘+leadDaysAfterCutoff일부터 고를 수 있다.
// 기준 시점은 "결제(입금) 완료" — 업체는 결제된 주문만 본다. 가상계좌는 입금이 늦으면 날짜를 못 맞춘다.
// 날짜·시각은 전부 KST 고정(브라우저 로컬 getter 금지). 서버가 같은 규칙으로 재검증한다
// (back src/gift-order/gift-order.delivery-date.ts, 명세 docs/gift-delivery-date-policy-spec.md) —
// 여기 계산은 안내·선택지용이고 최종 판정은 서버다. 규칙을 바꾸면 양쪽을 같이 바꿀 것.
// ─────────────────────────────────────────────

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'] as const;
const CUTOFF_RE = /^([01]\d|2[0-3]):([0-5]\d)$/;
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

/** 값이 비거나 깨진 정책을 화면이 바로 쓸 수 있게 채운 형태 */
export interface ActiveDeliveryDatePolicy {
  cutoffTime: string;
  leadDaysBeforeCutoff: number;
  leadDaysAfterCutoff: number;
  maxDaysAhead: number;
  closedWeekdays: number[];
  closedDates: string[];
  arrivalNotice: string;
}

export const DELIVERY_DATE_POLICY_DEFAULTS = {
  cutoffTime: '14:00',
  leadDaysBeforeCutoff: 1,
  leadDaysAfterCutoff: 2,
  maxDaysAhead: 14,
} as const;

const toNonNegativeInt = (value: unknown, fallback: number) =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0 ? value : fallback;

/**
 * 정책 정규화 — 꺼져 있거나 없으면 null(날짜 선택 UI 자체를 안 띄운다).
 * 깨진 값은 기본값으로 메우되, 마감 후 리드타임이 마감 전보다 짧아지지 않게 맞춘다.
 */
export const resolveActiveDatePolicy = (
  policy?: GiftDeliveryDatePolicy | null,
): ActiveDeliveryDatePolicy | null => {
  if (!policy?.enabled) return null;
  const before = toNonNegativeInt(
    policy.leadDaysBeforeCutoff,
    DELIVERY_DATE_POLICY_DEFAULTS.leadDaysBeforeCutoff,
  );
  const after = Math.max(
    before,
    toNonNegativeInt(
      policy.leadDaysAfterCutoff,
      DELIVERY_DATE_POLICY_DEFAULTS.leadDaysAfterCutoff,
    ),
  );
  return {
    cutoffTime:
      policy.cutoffTime && CUTOFF_RE.test(policy.cutoffTime)
        ? policy.cutoffTime
        : DELIVERY_DATE_POLICY_DEFAULTS.cutoffTime,
    leadDaysBeforeCutoff: before,
    leadDaysAfterCutoff: after,
    // 가장 빠른 날은 항상 고를 수 있어야 한다
    maxDaysAhead: Math.max(
      after,
      toNonNegativeInt(policy.maxDaysAhead, DELIVERY_DATE_POLICY_DEFAULTS.maxDaysAhead),
    ),
    closedWeekdays: (policy.closedWeekdays ?? []).filter(
      (d) => Number.isInteger(d) && d >= 0 && d <= 6,
    ),
    closedDates: (policy.closedDates ?? []).filter((d) => DATE_RE.test(d)),
    arrivalNotice: (policy.arrivalNotice ?? '').trim(),
  };
};

/** 상품(원상품 정보)에 딸린 populated 브랜드의 날짜 정책 */
export const datePolicyOfItemInfo = (
  info?: { giftItemBrand?: GiftItemBrand | string } | null,
): ActiveDeliveryDatePolicy | null => {
  const brand = info?.giftItemBrand;
  if (!brand || typeof brand !== 'object') return null;
  return resolveActiveDatePolicy(brand.deliveryDetail?.datePolicy);
};

/**
 * 한 주문에 날짜 지정 브랜드가 여럿이면 주문의 희망일 하나가 전부를 만족해야 한다 →
 * 가장 빡빡한 쪽으로 합친다(이른 마감·긴 리드타임·짧은 선택 범위·휴무 합집합).
 * 강조 문구는 첫 정책 것을 쓴다.
 */
export const mergeDatePolicies = (
  policies: (ActiveDeliveryDatePolicy | null | undefined)[],
): ActiveDeliveryDatePolicy | null => {
  const actives = policies.filter((p): p is ActiveDeliveryDatePolicy => !!p);
  const [first, ...rest] = actives;
  if (!first) return null;
  return rest.reduce<ActiveDeliveryDatePolicy>((acc, p) => {
    const leadDaysBeforeCutoff = Math.max(
      acc.leadDaysBeforeCutoff,
      p.leadDaysBeforeCutoff,
    );
    const leadDaysAfterCutoff = Math.max(acc.leadDaysAfterCutoff, p.leadDaysAfterCutoff);
    return {
      cutoffTime: p.cutoffTime < acc.cutoffTime ? p.cutoffTime : acc.cutoffTime,
      leadDaysBeforeCutoff,
      leadDaysAfterCutoff,
      maxDaysAhead: Math.max(
        leadDaysAfterCutoff,
        Math.min(acc.maxDaysAhead, p.maxDaysAhead),
      ),
      closedWeekdays: Array.from(new Set([...acc.closedWeekdays, ...p.closedWeekdays])),
      closedDates: Array.from(new Set([...acc.closedDates, ...p.closedDates])),
      arrivalNotice: acc.arrivalNotice || p.arrivalNotice,
    };
  }, first);
};

/** 'YYYY-MM-DD'의 요일 (0=일 … 6=토) — 달력 날짜 자체의 요일이라 시간대와 무관 */
export const weekdayOfDate = (date: string): number =>
  new Date(`${date}T00:00:00Z`).getUTCDay();

/** 지금이 마감시각을 지났는지 (KST). 마감시각 정각부터 "마감 후" */
export const isAfterCutoff = (policy: ActiveDeliveryDatePolicy, now: Date): boolean =>
  kstTimeHHmm(now) >= policy.cutoffTime;

const isClosedDate = (policy: ActiveDeliveryDatePolicy, date: string) =>
  policy.closedWeekdays.includes(weekdayOfDate(date)) ||
  policy.closedDates.includes(date);

export interface DeliveryDateOption {
  /** 'YYYY-MM-DD' (KST) */
  date: string;
  /** 0=일 … 6=토 */
  weekday: number;
  /** 오늘로부터 며칠 뒤 */
  daysFromToday: number;
  /** 휴무(요일·지정일)라 고를 수 없음 */
  closed: boolean;
}

export interface DeliveryDateChoices {
  afterCutoff: boolean;
  /** 리드타임상 가장 빠른 날(휴무 여부와 무관) */
  earliestByLead: string;
  /** 실제로 고를 수 있는 가장 빠른 날. 범위 안이 전부 휴무면 null */
  earliestSelectable: string | null;
  /** 리드타임상 가장 빠른 날부터 선택 범위 끝까지 (휴무일 포함 — 화면에서 비활성 표시) */
  options: DeliveryDateOption[];
}

/** 지금 주문할 때 고를 수 있는 날짜 목록 */
export const resolveDeliveryDateChoices = (
  policy: ActiveDeliveryDatePolicy,
  now: Date = new Date(),
): DeliveryDateChoices => {
  const today = kstDateOf(now);
  const afterCutoff = isAfterCutoff(policy, now);
  const lead = afterCutoff ? policy.leadDaysAfterCutoff : policy.leadDaysBeforeCutoff;
  const options: DeliveryDateOption[] = [];
  for (let offset = lead; offset <= policy.maxDaysAhead; offset++) {
    const date = kstShiftDay(today, offset);
    options.push({
      date,
      weekday: weekdayOfDate(date),
      daysFromToday: offset,
      closed: isClosedDate(policy, date),
    });
  }
  return {
    afterCutoff,
    earliestByLead: kstShiftDay(today, lead),
    earliestSelectable: options.find((o) => !o.closed)?.date ?? null,
    options,
  };
};

/** 고른 날짜가 지금 시각 기준으로도 유효한지 — 결제 버튼을 누르는 순간 다시 확인한다 */
export const isSelectableDeliveryDate = (
  policy: ActiveDeliveryDatePolicy,
  date: string | null | undefined,
  now: Date = new Date(),
): boolean => {
  if (!date || !DATE_RE.test(date)) return false;
  return resolveDeliveryDateChoices(policy, now).options.some(
    (o) => o.date === date && !o.closed,
  );
};

/** '14:00' → '오후 2시', '09:30' → '오전 9시 30분' */
export const cutoffTimeLabel = (cutoffTime: string): string => {
  const [h = 0, m = 0] = cutoffTime.split(':').map(Number);
  const period = h < 12 ? '오전' : '오후';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${period} ${hour12}시${m ? ` ${m}분` : ''}`;
};

/** 리드타임 일수 → '오늘' / '내일' / '모레' / 'N일 뒤' */
export const leadDaysLabel = (days: number): string =>
  days === 0 ? '오늘' : days === 1 ? '내일' : days === 2 ? '모레' : `${days}일 뒤`;

/** 서버 값(문자열 'YYYY-MM-DD' 또는 Date) → 'YYYY-MM-DD'(KST). 못 읽으면 빈 문자열 */
export const toDeliveryDateKey = (value?: string | Date | null): string => {
  if (!value) return '';
  if (typeof value === 'string' && DATE_RE.test(value)) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '' : kstDateOf(parsed);
};

/**
 * 배송 희망일 표기 '10월 2일(금)'. 서버 값이 문자열('YYYY-MM-DD')이 아니라 Date로 올 수도 있어
 * (구 데이터·인터셉터) 둘 다 받는다. 못 읽으면 빈 문자열.
 */
export const formatDeliveryDate = (value?: string | Date | null): string => {
  const date = toDeliveryDateKey(value);
  if (!date) return '';
  const [, month, day] = date.split('-');
  return `${Number(month)}월 ${Number(day)}일(${WEEKDAY_LABELS[weekdayOfDate(date)]})`;
};

/** 오늘(KST)부터 그 날짜까지 남은 일수 — 0=오늘, 1=내일, 음수=지남 */
export const daysUntilDeliveryDate = (date: string, now: Date = new Date()): number =>
  Math.round(
    (new Date(`${date}T00:00:00Z`).getTime() -
      new Date(`${kstDateOf(now)}T00:00:00Z`).getTime()) /
      86_400_000,
  );

/** 남은 일수 표기 — '오늘' / '내일' / '모레' / 'D-5' / '지남' */
export const deliveryDateRelativeLabel = (
  date: string,
  now: Date = new Date(),
): string => {
  const days = daysUntilDeliveryDate(date, now);
  if (days < 0) return '지남';
  return days <= 2 ? leadDaysLabel(days) : `D-${days}`;
};

/** 짧은 표기 '10/2(금)' — 표·칩용 */
export const formatDeliveryDateShort = (value?: string | Date | null): string => {
  const date = toDeliveryDateKey(value);
  if (!date) return '';
  const [, month, day] = date.split('-');
  return `${Number(month)}/${Number(day)}(${WEEKDAY_LABELS[weekdayOfDate(date)]})`;
};

/** 요일 한 글자 */
export const weekdayLabel = (weekday: number): string => WEEKDAY_LABELS[weekday] ?? '';

/**
 * 구매자 안내 한 줄 — "오후 2시 전 결제 시 내일부터, 이후는 모레부터 받을 수 있어요".
 * 기준은 주문서를 연 시각이 아니라 결제(입금) 완료 시각이다.
 */
export const deliveryDateRuleText = (policy: ActiveDeliveryDatePolicy): string => {
  const cutoff = cutoffTimeLabel(policy.cutoffTime);
  const before = leadDaysLabel(policy.leadDaysBeforeCutoff);
  const after = leadDaysLabel(policy.leadDaysAfterCutoff);
  return before === after
    ? `결제일 기준 ${before}부터 받을 수 있어요`
    : `${cutoff} 전 결제 시 ${before}부터, 이후는 ${after}부터 받을 수 있어요`;
};

/**
 * 고른 날짜에 받으려면 결제(입금)가 끝나야 하는 기한 — (받는 날 − 마감 전 리드타임)일의 마감시각.
 * 예: 내일(10/2) 수령, 리드타임 1일, 마감 14:00 → 10/1 14:00.
 */
export const resolvePaymentDeadline = (
  policy: ActiveDeliveryDatePolicy,
  date: string,
): Date =>
  new Date(
    `${kstShiftDay(date, -policy.leadDaysBeforeCutoff)}T${policy.cutoffTime}:00+09:00`,
  );

/**
 * 결제 기한이 오늘(KST)인가 — 기한 안내는 이때만 보여 준다.
 * 며칠 뒤 날짜를 고른 사람에게 "10월 5일 전까지 결제"는 지금 결제하는 상황에서 혼란만 준다.
 */
export const isPaymentDeadlineToday = (
  deadline?: Date | string | null,
  now: Date = new Date(),
): boolean => {
  if (!deadline) return false;
  const at = new Date(deadline);
  return !Number.isNaN(at.getTime()) && kstDateOf(at) === kstDateOf(now);
};

/** 결제 기한 표기 — '오늘 오후 2시' / '내일 오후 2시' / '10월 5일(월) 오후 2시'. 못 읽으면 빈 문자열 */
export const formatPaymentDeadline = (
  value?: Date | string | null,
  now: Date = new Date(),
): string => {
  if (!value) return '';
  const deadline = new Date(value);
  if (Number.isNaN(deadline.getTime())) return '';
  const date = kstDateOf(deadline);
  const days = daysUntilDeliveryDate(date, now);
  const dayLabel =
    days === 0 || days === 1 ? leadDaysLabel(days) : formatDeliveryDate(date);
  return `${dayLabel} ${cutoffTimeLabel(kstTimeHHmm(deadline))}`;
};

/**
 * 결제(입금) 완료가 기한을 넘겼는가 — 기한 "전"까지가 정상, 정각부터 지연.
 * 서버 판정(isDesiredDeliveryLate)과 같은 경계. 값이 없거나 못 읽으면 false.
 */
export const isPaymentPastDeadline = (
  payBy?: Date | string | null,
  at?: Date | string | null,
): boolean => {
  if (!payBy || !at) return false;
  const deadline = new Date(payBy).getTime();
  const moment = new Date(at).getTime();
  if (Number.isNaN(deadline) || Number.isNaN(moment)) return false;
  return moment >= deadline;
};

/**
 * 결제완료 화면의 받는 날 아래 안내 — 기준은 결제(입금) 완료 시각.
 * 기한을 넘겨 결제되면 서버가 받는 날을 결제 완료 시각 기준 가장 빠른 배송 가능일로 자동 변경한다.
 * - 입금 대기 중: 기한이 오늘이면 "오늘 ○시 전까지 입금", 기한이 지났으면 변경 예정 안내, 기한이 며칠 뒤면 안내 없음
 * - 결제 완료: 자동 변경된 건(requestedDate 있음)만 "○에서 ○로 변경", 그 외엔 안내 없음('')
 * 서버가 보내는 문자(입금 안내·결제완료)와 같은 내용이다.
 */
export const desiredDeliveryPaymentNotice = (
  order: {
    /** 지금 확정된 받는 날 (자동 변경됐으면 바뀐 날짜) */
    date?: string | Date | null;
    /** 구매자가 처음 고른 날짜 — 자동 변경된 주문에만 */
    requestedDate?: string | Date | null;
    /** 서버가 주문에 스냅샷한 결제 기한 (구서버·날짜 지정 아님 = 없음) */
    payBy?: Date | string | null;
    /** 가상계좌·계좌입금으로 아직 입금을 기다리는 주문인지 */
    awaitingDeposit: boolean;
  },
  now: Date = new Date(),
): string => {
  if (order.awaitingDeposit) {
    const payByLabel = formatPaymentDeadline(order.payBy, now);
    if (!payByLabel) return '입금이 늦으면 받으실 날짜가 늦춰집니다.';
    if (isPaymentPastDeadline(order.payBy, now)) {
      return '입금 기한이 지났습니다. 받으실 날짜는 입금 후 가장 빠른 날로 바뀝니다.';
    }
    // 기한이 오늘일 때만 재촉한다 — 며칠 뒤 기한은 화면에 띄우지 않는다(입금 안내 문자에는 있음)
    return isPaymentDeadlineToday(order.payBy, now)
      ? `${payByLabel} 전까지 입금해 주세요. 늦으면 날짜가 늦춰집니다.`
      : '';
  }
  const requested = formatDeliveryDate(order.requestedDate);
  const settled = formatDeliveryDate(order.date);
  return requested && settled && requested !== settled
    ? `결제가 늦어 받으실 날짜가 ${requested}에서 ${settled}로 바뀌었습니다.`
    : '';
};

/**
 * 달력에서 고를 수 없는 날을 눌렀을 때 보여 줄 이유 한 줄. 고를 수 있는 날이면 빈 문자열.
 * - 마감시각이 지나 밀린 날: "오후 2시 결제 마감이 지나 10월 3일(토)부터 받을 수 있어요."
 * - 그보다 이른 날(오늘·지난 날 등): "10월 3일(토)부터 받을 수 있어요."
 * - 휴무일: "10월 4일(일)은 배송이 없는 날이에요."
 * - 선택 범위 뒤: "10월 15일(목)까지만 고를 수 있어요."
 */
export const deliveryDateBlockedReason = (
  policy: ActiveDeliveryDatePolicy,
  choices: DeliveryDateChoices,
  date: string,
): string => {
  const option = choices.options.find((o) => o.date === date);
  if (option && !option.closed) return '';
  if (option?.closed) return `${formatDeliveryDate(date)}은 배송이 없는 날이에요.`;
  const earliest = choices.earliestSelectable;
  if (!earliest) return '';
  if (date < choices.earliestByLead) {
    // 마감 전이었다면 고를 수 있었던 날 = 마감 때문에 밀린 날
    const lostByCutoff =
      choices.afterCutoff &&
      date >=
        kstShiftDay(
          choices.earliestByLead,
          policy.leadDaysBeforeCutoff - policy.leadDaysAfterCutoff,
        );
    return `${
      lostByCutoff ? `${cutoffTimeLabel(policy.cutoffTime)} 결제 마감이 지나 ` : ''
    }${formatDeliveryDate(earliest)}부터 받을 수 있어요.`;
  }
  const last = [...choices.options].reverse().find((o) => !o.closed)?.date;
  return last ? `${formatDeliveryDate(last)}까지만 고를 수 있어요.` : '';
};
